/**
 * Combined Properties & Groups display formatting
 * Used by Thread UI, Email, and SMS summaries
 */
import { formatRelationshipTypeLabel } from './formatting-utils.js';
/**
 * Format source labels for display
 * Always shows source: (CRM), (Mentioned), or (CRM, Mentioned)
 */
function formatSourceLabel(sources) {
    if (!sources || sources.length === 0) {
        return '';
    }
    const hasCRM = sources.includes('CRM');
    const hasMentioned = sources.includes('MENTIONED');
    if (hasCRM && hasMentioned) {
        return ' (CRM, Mentioned)';
    }
    else if (hasCRM) {
        return ' (CRM)';
    }
    else if (hasMentioned) {
        return ' (Mentioned)';
    }
    return '';
}
/**
 * Format combined Properties & Groups for display
 * Used by Thread UI, Email, and SMS summaries
 *
 * Format: "[Group] of [Property], [Unit] (Source)" joined by " · "
 * Source is always shown: (CRM), (Mentioned), or (CRM, Mentioned)
 *
 * Examples:
 * - "Owner of 96 Lefferts Place (CRM) · 94 Lefferts Place (Mentioned) · Vendor (Mentioned)"
 * - "Tenant of 123 Main St, Unit 4B (CRM, Mentioned)"
 * - "94 Lefferts Place (Mentioned)" (property with no group)
 * - "Vendor (Mentioned)" (group with no property)
 */
export function formatPropertiesAndGroupsDisplay(propertyMatches, groupMatches) {
    const properties = propertyMatches || [];
    const groups = groupMatches || [];
    if (properties.length === 0 && groups.length === 0) {
        return 'None';
    }
    const parts = [];
    // Track which property sources have been covered by groups
    // Key: propertyId, Value: Set of sources covered by groups
    const coveredPropertySources = new Map();
    // 1. Groups WITH property associations: "Owner of 94 Lefferts Place (CRM)"
    for (const group of groups) {
        if (group.matchedAssociationId) {
            const matchingProperty = properties.find(p => p.matchedAssociationId === group.matchedAssociationId);
            // Track which sources this group covers for this property
            if (!coveredPropertySources.has(group.matchedAssociationId)) {
                coveredPropertySources.set(group.matchedAssociationId, new Set());
            }
            group.sources?.forEach(s => coveredPropertySources.get(group.matchedAssociationId).add(s));
            const groupLabel = group.groupType
                ? formatRelationshipTypeLabel(group.groupType)
                : group.customGroup;
            let part = `${groupLabel} of ${group.propertyName || 'Unknown Property'}`;
            if (matchingProperty?.unit) {
                part += `, ${matchingProperty.unit}`;
            }
            // Use only the GROUP's sources (merging happens upstream in SessionGroupTagService)
            part += formatSourceLabel(group.sources);
            parts.push(part);
        }
    }
    // 2. Properties - show if they have sources not covered by any group
    for (const property of properties) {
        const propertyId = property.matchedAssociationId || '';
        const coveredSources = coveredPropertySources.get(propertyId);
        const propertySources = property.sources || [];
        // Find sources on the property that aren't covered by any group
        const uncoveredSources = propertySources.filter(s => !coveredSources || !coveredSources.has(s));
        // If property has uncovered sources, or no group referenced this property at all
        if (uncoveredSources.length > 0 || !coveredSources) {
            let part = property.propertyName || 'Unknown Property';
            if (property.unit) {
                part += `, ${property.unit}`;
            }
            // Show only the uncovered sources (or all if no group covered it)
            const sourcesToShow = coveredSources ? uncoveredSources : propertySources;
            part += formatSourceLabel(sourcesToShow);
            parts.push(part);
        }
    }
    // 3. Groups WITHOUT property associations: "Vendor (Mentioned)"
    for (const group of groups) {
        if (!group.matchedAssociationId) {
            const groupLabel = group.groupType
                ? formatRelationshipTypeLabel(group.groupType)
                : group.customGroup;
            let part = groupLabel || 'Unknown Group';
            part += formatSourceLabel(group.sources);
            parts.push(part);
        }
    }
    return parts.length > 0 ? parts.join(' · ') : 'None';
}
//# sourceMappingURL=property-group-display.js.map